import { AlertTriangle, FileSearch, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useLocale } from '@/i18n/locale-provider';
import { NuatMark, Badge } from '@/components/ui';
import type { ChatMessage, VerificationStatus } from '@/lib/types';

/** Tone is a property of the status itself; only the wording is translated. */
const VERIFICATION_TONE: Record<VerificationStatus, 'good' | 'warn' | 'bad' | 'neutral'> = {
  MATCH: 'good',
  PARTIAL_MATCH: 'warn',
  NEEDS_REVIEW: 'bad',
  SOURCE_UNAVAILABLE: 'bad',
  UNVERIFIED: 'neutral',
};

export function MessageBubble({
  message, onInspect, onRate, isInspecting,
}: {
  message: ChatMessage;
  onInspect: () => void;
  onRate: (message: ChatMessage, rating: 'HELPFUL' | 'NOT_HELPFUL') => void;
  isInspecting: boolean;
}) {
  const { t, n, fill } = useLocale();

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-ee-sm bg-deep-500 px-4 py-2.5 text-sm text-white">
          {message.content}
        </p>
      </div>
    );
  }

  const sourceCount = message.sources?.length ?? 0;
  const status = message.verificationStatus in VERIFICATION_TONE
    ? message.verificationStatus
    : ('UNVERIFIED' as VerificationStatus);

  return (
    <div className="flex gap-3">
      <NuatMark size={28} className="mt-1 shrink-0" />

      <div className="min-w-0 flex-1">
        <div
          className={`rounded-2xl rounded-ss-sm border px-4 py-3 ${
            message.errorCode
              ? 'border-danger-border bg-danger-bg'
              : isInspecting
                ? 'border-deep-300 bg-deep-50'
                : 'border-mist-200 bg-mist-50'
          }`}
        >
          <div className="prose-nuat whitespace-pre-wrap text-sm leading-relaxed text-mist-800">
            {message.content}
          </div>

          {message.warnings && message.warnings.length > 0 && (
            <ul className="mt-3 space-y-1 border-t border-mist-200 pt-3">
              {message.warnings.map((warning, index) => (
                <li key={index} className="flex items-start gap-1.5 text-xs text-warning-fg">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden />
                  {warning}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          {sourceCount > 0 && (
            <button
              type="button"
              onClick={onInspect}
              className="inline-flex items-center gap-1.5 rounded-md border border-mist-300 bg-white px-2.5 py-1
                         font-medium text-deep-600 transition-colors hover:border-deep-300 hover:bg-deep-50"
            >
              <FileSearch size={13} aria-hidden />
              {sourceCount === 1 ? t.message.sourcesOne : fill(t.message.sources, { count: n(sourceCount) })}
              {message.calculations?.length
                ? ` · ${fill(t.message.figures, { count: n(message.calculations.length) })}`
                : ''}
            </button>
          )}

          <Badge tone={VERIFICATION_TONE[status]}>{t.message.verification[status]}</Badge>

          {message.confidence !== null && (
            <span className="text-mist-500">
              {fill(t.message.confidence, { percent: n(Math.round(message.confidence * 100)) })}
            </span>
          )}
          {message.latencyMs !== null && (
            <span className="tabular text-mist-400">{n(message.latencyMs)} ms</span>
          )}
          {message.model && message.model !== 'none' && (
            <span className="ltr-embed text-mist-400">{message.model}</span>
          )}

          {!message.errorCode && (
            <span className="ms-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => onRate(message, 'HELPFUL')}
                aria-label={t.message.helpful}
                aria-pressed={message.feedbackGiven === 'HELPFUL'}
                className={`rounded p-1.5 transition-colors ${
                  message.feedbackGiven === 'HELPFUL'
                    ? 'bg-success-bg text-success-fg'
                    : 'text-mist-400 hover:bg-mist-100 hover:text-mist-600'
                }`}
              >
                <ThumbsUp size={13} />
              </button>
              <button
                type="button"
                onClick={() => onRate(message, 'NOT_HELPFUL')}
                aria-label={t.message.notHelpful}
                aria-pressed={message.feedbackGiven === 'NOT_HELPFUL'}
                className={`rounded p-1.5 transition-colors ${
                  message.feedbackGiven === 'NOT_HELPFUL'
                    ? 'bg-danger-bg text-danger-fg'
                    : 'text-mist-400 hover:bg-mist-100 hover:text-mist-600'
                }`}
              >
                <ThumbsDown size={13} />
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
